import { Box, Collapse } from '@mui/material';
import AddressLookup from '@acorn-portal/shared/src/components/addressLookup';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import { Form, Formik } from 'formik';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Address } from '@acorn-portal/shared/src/services/api';
import { updateChangeOfAddress } from '@acorn-portal/shared/src/store/renewalChanges.slice';

import { isUsedForBusiness } from '@acorn-portal/shared/src/utils';
import {
	ANNUAL_MILEAGE,
	BUSINESS_MILEAGE,
	OVERNIGHT_ADDRESS_DIFFERENT,
} from '@acorn-portal/shared/src/utils/modalText';
import getClosestMileage from '@acorn-portal/shared/src/utils/getClosestMileage';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import useRateRenewal from '@acorn-portal/shared/src/hooks/useRateRenewal';
import useRenewalNavigation from '@acorn-portal/shared/src/hooks/useRenewalNavigation';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import AutoCompleteFormField from '@acorn-portal/shared/src/components/inputs/AutoCompleteFormField';
import ChangeOfAddressSchema from '@acorn-portal/shared/src/utils/validation/ChangeOfAddressSchema';
import hasOverNightAddress from '../../utils/has-overnight-address';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

export interface IFormChangeAddress {
	isHomeOwner: string;
	address: Address;
	annualMileage: string;
	businessMileage: string;
	keptOvernight: string;
	overnightAddress: Address;
	licencingAuthority?: string;
	operatingArea?: string;
	overnightAddressIsAway: string;
}
const ChangeOfAddress: FC = () => {
	const [open, toggleModal] = useState(false);
	const { goToRenewalHome } = useRenewalNavigation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [loading, setLoading] = useState(false);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const lists = useAppSelector((redux) => redux.referenceData);
	const renewalState = useAppSelector((redux) => redux.renewal);
	const { rateRenewal, loadingRenewalQuote } = useRateRenewal();
	const isTaxi = fullPolicy.product === Product.TAXI;

	useEffect(() => {
		setLoading(loadingRenewalQuote);
		if (!renewalState.policyReference) {
			navigate(ROUTE_PATHS.home);
		}
	}, [loadingRenewalQuote]);

	if (loading)
		return (
			<JourneyCardLayout pageTitle='' loading={loading}>
				<div />
			</JourneyCardLayout>
		);

	const initialValues: IFormChangeAddress = {
		isHomeOwner: renewalState.policyDetails.policyholder.isHomeOwner ? 'yes' : 'no',
		address: {
			...renewalState.policyDetails.policyholder.address,
			postcode: renewalState.policyDetails.policyholder.address.postcode ?? undefined,
			line2: renewalState.policyDetails.policyholder.address.line2 ?? undefined,
			line3: renewalState.policyDetails.policyholder.address.line3 ?? undefined,
			line4: renewalState.policyDetails.policyholder.address.line3 ?? undefined,
		},
		licencingAuthority:
			renewalState.vehicleDetails.licencingAuthority ?? fullPolicy.vehicleDetails.licencingAuthority ?? '',
		operatingArea: renewalState.policyDetails.operatingArea ?? fullPolicy.coverDetails.operatingArea ?? '',
		overnightAddress: {
			...(renewalState.vehicleDetails.overnightAddress || {
				line1: '',
				city: '',
				postcode: '',
			}),
		},
		annualMileage: getClosestMileage(
			lists.millageIncrementOptions as IOptions[],
			renewalState.vehicleDetails.annualMileage,
		),
		businessMileage: renewalState.vehicleDetails.businessMileage?.toString() || '',
		keptOvernight: renewalState.vehicleDetails.vehicleOvernight || '',
		overnightAddressIsAway: hasOverNightAddress(
			renewalState.policyDetails.policyholder.address,
			renewalState.vehicleDetails.overnightAddress ?? ({} as Address),
		)
			? 'yes'
			: 'no',
	};

	const GetBusinessMillageOptions = (annualMileage: string) => {
		return (
			lists.millageIncrementOptions.filter((option) => (option.value as number) <= parseInt(annualMileage, 10)) || []
		);
	};

	const handleOnSubmit = async (values: IFormChangeAddress) => {
		setLoading(true);

		let overnightAddress = {
			line1: values.overnightAddress.line1,
			line2: values.overnightAddress.line2 ?? undefined,
			line3: values.overnightAddress.line3 ?? undefined,
			line4: values.overnightAddress.line4 ?? undefined,
			city: values.overnightAddress.city,
			postcode: values.overnightAddress.postcode,
		};
		if (values.overnightAddressIsAway === 'no') {
			overnightAddress = {
				line1: values.address.line1,
				line2: values.address.line2 ?? undefined,
				line3: values.address.line3 ?? undefined,
				line4: values.address.line4 ?? undefined,
				city: values.address.city,
				postcode: values.address.postcode,
			};
		}

		try {
			const renewalUpdated = {
				...renewalState,
				vehicleDetails: {
					...renewalState.vehicleDetails,
					annualMileage: values.annualMileage ? parseInt(values.annualMileage, 10) : 0,
					businessMileage: values.businessMileage ? parseInt(values.businessMileage.toString(), 10) : 0,
					vehicleOvernight: values.keptOvernight,
					overnightAddress: overnightAddress,
					licencingAuthority: values.licencingAuthority ?? undefined,
				},
				policyDetails: {
					...renewalState.policyDetails,
					policyholder: {
						address: {
							postcode: values.address.postcode,
							line1: values.address.line1,
							line2: values.address.line2,
							line3: values.address.line3,
							city: values.address.city,
							county: values.address.county ?? '',
							country: values.address.country ?? '',
						},
						isHomeOwner: values.isHomeOwner === 'yes',
					},
					operatingArea: values.operatingArea ?? '',
				},
				overnightAddressIsAway: values.overnightAddressIsAway || '',
			};

			dispatch(updateChangeOfAddress(true));
			await rateRenewal({ renewalState: renewalUpdated, fullPolicy });
		} catch (error) {
			console.error('Error during change of address:', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	return (
		<JourneyCardLayout pageTitle='Change Of Address' loading={loading}>
			<Formik
				initialValues={initialValues}
				validationSchema={ChangeOfAddressSchema(renewalState, fullPolicy)}
				onSubmit={handleOnSubmit}
				key={JSON.stringify(renewalState.newRenewalTransactionId)}
			>
				{(formik) => {
					return (
						<Form>
							<FormikLogErrors />
							<AddressLookup
								initialValue={formik.values.address}
								onChange={(value) => {
									formik.setFormikState((formState) => ({
										...formState,
										values: {
											...formState.values,
											address: {
												...formState.values.address,
												line1: value?.line1 ?? '',
												line2: value?.line2,
												line3: value?.line3,
												line4: value?.line4,
												city: value?.city ?? '',
												county: value?.county ?? '',
												country: value?.country ?? '',
												postcode: value?.postcode ?? '',
											},
										},
									}));
								}}
								globalError={formik.touched.address?.line1 && Boolean(formik.errors.address?.line1)}
							/>
							{!isTaxi && (
								<>
									<SelectFormField
										label='Annual mileage'
										name='annualMileage'
										id='annualMileage'
										value={formik.values.annualMileage}
										onChange={(value) => {
											formik.setFieldValue('annualMileage', value.toString());
										}}
										options={lists.millageIncrementOptions as IOptions[]}
										modal={{
											title: ANNUAL_MILEAGE.header,
											description: ANNUAL_MILEAGE.text,
										}}
										error={formik.touched.annualMileage && Boolean(formik.errors.annualMileage)}
										helperText={formik.touched.annualMileage && formik.errors.annualMileage}
									/>

									<Collapse in={isUsedForBusiness(renewalState.vehicleDetails.vehicleUse)}>
										<Box>
											<SelectFormField
												label='Business mileage'
												name='businessMileage'
												id='businessMileage'
												value={formik.values.businessMileage}
												onChange={(value) => formik.setFieldValue('businessMileage', value.toString())}
												options={GetBusinessMillageOptions(formik.values.annualMileage) as IOptions[]}
												modal={{
													title: BUSINESS_MILEAGE.header,
													description: BUSINESS_MILEAGE.text,
												}}
												error={formik.touched.businessMileage && Boolean(formik.errors.businessMileage)}
												helperText={formik.touched.businessMileage && formik.errors.businessMileage}
											/>
										</Box>
									</Collapse>
								</>
							)}
							{isTaxi && (
								<>
									<AutoCompleteFormField
										label='Please select your licensing authority'
										name='licencingAuthority'
										id='licencingAuthority'
										value={formik.values.licencingAuthority}
										onChange={(value) => formik.setFieldValue('licencingAuthority', value)}
										options={lists.licencingArea as IOptions[]}
										error={formik.touched.licencingAuthority && Boolean(formik.errors.licencingAuthority)}
										helperText={formik.touched.licencingAuthority && formik.errors.licencingAuthority}
									/>
									<AutoCompleteFormField
										label='Primary operating area'
										name='operatingArea'
										id='operatingArea'
										value={formik.values.operatingArea}
										onChange={(value) => formik.setFieldValue('operatingArea', value)}
										options={lists.licencingArea as IOptions[]}
										error={formik.touched.operatingArea && Boolean(formik.errors.operatingArea)}
										helperText={formik.touched.operatingArea && formik.errors.operatingArea}
									/>
								</>
							)}
							<ToggleButtonFormField
								label='Where is the vehicle kept overnight?'
								name='keptOvernight'
								id='keptOvernight'
								value={formik.values.keptOvernight}
								onChange={(value) => formik.setFieldValue('keptOvernight', value)}
								options={lists.vehicleStorageOptions as IOptions[]}
								error={formik.touched.keptOvernight && Boolean(formik.errors.keptOvernight)}
								helperText={formik.touched.keptOvernight && formik.errors.keptOvernight}
							/>
							<RadioGroupButton
								label="Is the overnight address different from the policyholder's address?"
								name='overnightAddressIsAway'
								id='overnightAddressIsAway'
								value={formik.values.overnightAddressIsAway}
								onChange={(value) => {
									formik.setFieldValue('overnightAddressIsAway', value);
								}}
								error={formik.touched.overnightAddressIsAway && Boolean(formik.errors.overnightAddressIsAway)}
								helperText={formik.touched.overnightAddressIsAway && formik.errors.overnightAddressIsAway}
								modal={{
									title: OVERNIGHT_ADDRESS_DIFFERENT.title,
									description: OVERNIGHT_ADDRESS_DIFFERENT.description,
								}}
							/>
							<Collapse in={formik.values.overnightAddressIsAway === 'yes'}>
								<AddressLookup
									initialValue={formik.values.overnightAddress}
									firstFieldLabel='Overnight House number or Name'
									postcodeLabel='Overnight Postcode'
									onChange={(value) => {
										formik.setFieldValue('overnightAddress', {
											...formik.values.overnightAddress,
											line1: value?.line1 ?? '',
											line2: value?.line2 ?? undefined,
											line3: value?.line3 ?? undefined,
											line4: value?.line4 ?? undefined,
											city: value?.city ?? '',
											postcode: value?.postcode ?? '',
										});
									}}
									globalError={formik.touched.address?.line1 && Boolean(formik.errors.address?.line1)}
								/>
							</Collapse>
							<RadioGroupButton
								label='Are you a homeowner?'
								name='isHomeOwner'
								id='isHomeOwner'
								value={formik.values.isHomeOwner}
								onChange={(value) => {
									formik.setFieldValue('isHomeOwner', value);
								}}
								error={formik.touched.isHomeOwner && Boolean(formik.errors.isHomeOwner)}
								helperText={formik.touched.isHomeOwner && formik.errors.isHomeOwner}
							/>
							<ActionButtons>
								<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
								<ContinueButton type='submit'>Continue</ContinueButton>
							</ActionButtons>
							<HelperModal
								open={open}
								onClose={() => {
									toggleModal(false);
								}}
								title='Discard The Changes?'
								description='Are you sure you wish to discard the changes?'
								closeText='No'
								submitText='Yes'
								onSubmit={() => {
									formik.resetForm();
									goToRenewalHome();
								}}
							/>
						</Form>
					);
				}}
			</Formik>
		</JourneyCardLayout>
	);
};

export default ChangeOfAddress;
